import {
  AcousticReceiver,
  defaultConfig,
  type AcousticConfig,
  type ReceiverState,
} from '../../core';
import { Spectrum } from '../components/Spectrum';
import { Waterfall } from '../components/Waterfall';
import { LedMeter } from '../components/LedMeter';
import { SoftKey } from '../components/SoftKey';
import { SymbolStream, type SymbolKind } from '../components/SymbolStream';

type ConfigGetter = () => Partial<AcousticConfig>;
type LogFn = (msg: string) => void;
type StateFn = (state: ReceiverState) => void;

const PULSE_MS = 1800;

export class ReceiverView {
  readonly el: HTMLElement;
  private spectrum: Spectrum;
  private waterfall: Waterfall;
  private meter: LedMeter;
  private symbolStream: SymbolStream;
  private btn: SoftKey;
  private armActionBtn: SoftKey;
  private importBtn: SoftKey;
  private armWrap: HTMLDivElement;
  private monitorToggle: HTMLInputElement;
  private fileInput: HTMLInputElement;
  private statusBadge: HTMLSpanElement;
  private progressFill: HTMLDivElement;
  private progressValue: HTMLSpanElement;
  private outputEl: HTMLDivElement;
  private tagRow: HTMLDivElement;
  private pulseTimer: number | null = null;

  readonly receiver = new AcousticReceiver();
  private getConfig: ConfigGetter;
  private log: LogFn;
  private onStateChanged: StateFn | null = null;
  private listening = false;
  private playingFile = false;
  private analyserPoll: number | null = null;

  constructor(getConfig: ConfigGetter, log: LogFn, onStateChanged?: StateFn) {
    this.getConfig = getConfig;
    this.log = log;
    this.onStateChanged = onStateChanged ?? null;

    this.el = document.createElement('section');
    this.el.className = 'panel';
    this.el.dataset.kind = 'receiver';

    const body = document.createElement('div');
    body.className = 'panel__body';

    // === ARM RX wrap (bar + drawer panel) ===
    this.armWrap = document.createElement('div');
    this.armWrap.className = 'rx-arm';
    this.armWrap.dataset.state = 'idle';

    const armBar = document.createElement('div');
    armBar.className = 'rx-arm__bar';
    this.btn = new SoftKey('ARM RX', 'arm');
    this.btn.el.classList.add('rx-arm__open-btn');
    this.statusBadge = document.createElement('span');
    this.statusBadge.className = 'panel-state';
    this.statusBadge.dataset.state = 'idle';
    this.statusBadge.textContent = 'IDLE';
    armBar.appendChild(this.btn.el);
    armBar.appendChild(this.statusBadge);
    this.armWrap.appendChild(armBar);

    const armPanel = document.createElement('div');
    armPanel.className = 'rx-arm__panel';

    const headerRow = document.createElement('div');
    headerRow.className = 'rx-arm__row';
    const headerLabel = document.createElement('span');
    headerLabel.className = 'form-label';
    headerLabel.textContent = 'Receiver';
    headerRow.appendChild(headerLabel);

    // Monitor lives in the header (only affects WAV import audibility).
    const toggleLabel = document.createElement('label');
    toggleLabel.className = 'rx-listen-toggle rx-listen-toggle--inline';
    this.monitorToggle = document.createElement('input');
    this.monitorToggle.type = 'checkbox';
    this.monitorToggle.id = 'rx-monitor-toggle';
    const toggleBox = document.createElement('span');
    toggleBox.className = 'rx-listen-toggle__box';
    toggleBox.setAttribute('aria-hidden', 'true');
    const toggleText = document.createElement('span');
    toggleText.className = 'rx-listen-toggle__label';
    toggleText.textContent = 'Monitor';
    toggleLabel.appendChild(this.monitorToggle);
    toggleLabel.appendChild(toggleBox);
    toggleLabel.appendChild(toggleText);
    headerRow.appendChild(toggleLabel);

    const closeBtn = document.createElement('span');
    closeBtn.className = 'rx-arm__close';
    closeBtn.setAttribute('role', 'button');
    closeBtn.tabIndex = 0;
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    headerRow.appendChild(closeBtn);

    armPanel.appendChild(headerRow);

    this.outputEl = document.createElement('div');
    this.outputEl.className = 'rx-output';
    this.outputEl.id = 'rx-output';
    armPanel.appendChild(this.outputEl);

    this.tagRow = document.createElement('div');
    this.tagRow.className = 'tag-row';
    armPanel.appendChild(this.tagRow);

    const armActions = document.createElement('div');
    armActions.className = 'rx-arm__actions';
    this.importBtn = new SoftKey('IMPORT WAV', 'ghost');
    this.armActionBtn = new SoftKey('ARM RX', 'arm');
    armActions.appendChild(this.importBtn.el);
    armActions.appendChild(this.armActionBtn.el);
    armPanel.appendChild(armActions);

    this.armWrap.appendChild(armPanel);

    closeBtn.addEventListener('click', () => this.setArmOpen(false));

    // === Hidden file input for IMPORT WAV ===
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = 'audio/wav,audio/x-wav,audio/*';
    this.fileInput.style.display = 'none';

    // === Progress row (above the spectrum, mirroring TX layout) ===
    const progress = document.createElement('div');
    progress.className = 'progress-row';
    progress.innerHTML = `
      <div class="progress-bar"><div class="progress-bar__fill"></div></div>
      <span class="progress-row__value">0%</span>
    `;
    this.progressFill = progress.querySelector<HTMLDivElement>('.progress-bar__fill')!;
    this.progressValue = progress.querySelector<HTMLSpanElement>('.progress-row__value')!;

    // === Visualizer blocks ===
    const vizBlock = document.createElement('div');
    vizBlock.className = 'viz-block';
    vizBlock.innerHTML = `
      <div class="viz-block__head">
        <span class="viz-block__title">Spectrum</span>
        <span class="viz-block__sub">0 — 6.0 kHz</span>
      </div>
      <div class="viz-block__canvas"></div>
    `;
    this.spectrum = new Spectrum();
    vizBlock.querySelector<HTMLDivElement>('.viz-block__canvas')!.appendChild(this.spectrum.el);

    const wfBlock = document.createElement('div');
    wfBlock.className = 'viz-block';
    wfBlock.innerHTML = `
      <div class="viz-block__head">
        <span class="viz-block__title">Waterfall</span>
        <span class="viz-block__sub">↓ TIME</span>
      </div>
      <div class="viz-block__canvas viz-block__canvas--waterfall"></div>
    `;
    this.waterfall = new Waterfall();
    wfBlock.querySelector<HTMLDivElement>('.viz-block__canvas')!.appendChild(this.waterfall.el);

    this.meter = new LedMeter();

    const streamBlock = document.createElement('div');
    streamBlock.className = 'viz-block';
    streamBlock.innerHTML = `
      <div class="viz-block__head">
        <span class="viz-block__title">Symbol Stream</span>
        <span class="viz-block__sub">4-FSK · RX</span>
      </div>
      <div class="viz-block__canvas viz-block__canvas--symbols"></div>
    `;
    this.symbolStream = new SymbolStream();
    streamBlock.querySelector<HTMLDivElement>('.viz-block__canvas')!.appendChild(this.symbolStream.el);

    // === Final body order: arm-drawer → progress → spectrum → waterfall → meter → symbol-stream ===
    body.appendChild(this.armWrap);
    body.appendChild(this.fileInput);
    body.appendChild(progress);
    body.appendChild(vizBlock);
    body.appendChild(wfBlock);
    body.appendChild(this.meter.el);
    body.appendChild(streamBlock);

    this.el.appendChild(body);

    this.bindEvents();
    this.bindLifecycleHooks();
    this.spectrum.start();
    this.waterfall.start();
    this.meter.start();
    this.symbolStream.start();
  }

  private bindLifecycleHooks(): void {
    const teardown = () => {
      if (this.listening || this.playingFile) this.stopRx();
    };
    window.addEventListener('pagehide', teardown);
    window.addEventListener('beforeunload', teardown);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && (this.listening || this.playingFile)) this.stopRx();
    });
  }

  applyConfig(): void {
    const cfg = this.getConfig();
    if (cfg.freqs) {
      this.spectrum.freqs = cfg.freqs;
      this.waterfall.freqs = cfg.freqs;
    }
  }

  private bindEvents(): void {
    this.btn.onClick(() => this.handleArmBtn());
    this.armActionBtn.onClick(() => this.handleArmAction());
    this.importBtn.onClick(() => this.handleImportClick());
    this.fileInput.addEventListener('change', () => this.handleFileChosen());
    this.monitorToggle.addEventListener('change', () => {
      this.receiver.setAudible(this.monitorToggle.checked);
    });

    this.receiver.onStateChange = (state) => {
      this.statusBadge.dataset.state = state;
      this.statusBadge.textContent = state.toUpperCase();
      if (state === 'calibrating' || state === 'listening') {
        const a = this.receiver.getAnalyser();
        const sr = this.receiver.audioCtx?.sampleRate ?? 48000;
        if (a) {
          this.spectrum.bind(a, sr);
          this.waterfall.bind(a, sr);
          this.meter.bind(a);
        }
      }
      // Receiver returned to idle on its own (e.g. imported WAV finished playing
      // and the post-playback grace-period elapsed). Reset the View UI.
      if (state === 'idle' && (this.listening || this.playingFile)) {
        this.stopRx();
      }
      this.onStateChanged?.(state);
    };
    this.receiver.onProgress = (current, total) => {
      const pct = total === 0 ? 0 : Math.round((current / total) * 100);
      this.progressFill.style.width = pct + '%';
      this.progressValue.textContent = pct + '%';
    };
    this.receiver.onFrameComplete = (payload, crcOk, result) => {
      this.outputEl.textContent = payload;
      this.tagRow.innerHTML = '';
      this.tagRow.appendChild(
        this.tag(`${result.length ?? 0} B`, 'default'),
      );
      this.tagRow.appendChild(
        this.tag(result.compressed ? 'deflate' : 'raw', 'default'),
      );
      this.tagRow.appendChild(
        this.tag(`CRC ${crcOk ? 'OK' : 'FAIL'}`, crcOk ? 'ok' : 'fail'),
      );
      if (result.corrected > 0) {
        this.tagRow.appendChild(
          this.tag(
            `${result.corrected} bit${result.corrected > 1 ? 's' : ''} corrected`,
            'warn',
          ),
        );
      }
      this.setArmOpen(true);
      this.pulse(crcOk ? 'ok' : 'error');
    };
    this.receiver.onError = (msg) => {
      this.tagRow.innerHTML = '';
      this.tagRow.appendChild(this.tag(msg, 'fail'));
      this.log('[RX] Error: ' + msg);
      this.setArmOpen(true);
      this.pulse('error');
    };
    this.receiver.onDebug = (msg) => this.log('[RX] ' + msg);
    this.receiver.onSymbol = (sym, state) => {
      const kind: SymbolKind =
        state === 'preamble' ? 'preamble' :
        state === 'sync' ? 'sync' :
        state === 'receiving' ? 'data' :
        state === 'listening' ? 'listening' :
        'detected';
      this.symbolStream.pushSymbol(sym, kind);
    };
    this.receiver.onPlaybackEnd = () => {
      this.log('[RX] File playback ended');
    };
  }

  private setArmOpen(open: boolean): void {
    this.armWrap.classList.toggle('is-open', open);
    this.btn.setLabel(open ? 'ARM RX ▾' : 'ARM RX');
  }

  private pulse(tone: 'ok' | 'error'): void {
    if (this.pulseTimer !== null) {
      window.clearTimeout(this.pulseTimer);
      this.pulseTimer = null;
    }
    this.armWrap.dataset.tone = tone;
    this.armWrap.classList.remove('is-pulsing');
    // Force reflow so the animation restarts even when pulsing back-to-back.
    void this.armWrap.offsetWidth;
    this.armWrap.classList.add('is-pulsing');
    this.pulseTimer = window.setTimeout(() => {
      this.armWrap.classList.remove('is-pulsing');
      this.pulseTimer = null;
    }, PULSE_MS);
  }

  private stopRx(): void {
    // Clear flags before receiver.stop() so the onStateChange('idle')
    // callback that fires from inside stop() doesn't recursively call us.
    this.listening = false;
    this.playingFile = false;
    this.armWrap.dataset.state = 'idle';
    this.receiver.stop();
    this.spectrum.bind(null);
    this.waterfall.bind(null);
    this.meter.bind(null);
    this.armActionBtn.setLabel('ARM RX');
    this.armActionBtn.setVariant('arm');
    this.statusBadge.dataset.state = 'idle';
    this.statusBadge.textContent = 'IDLE';
    this.progressFill.style.width = '0%';
    this.progressValue.textContent = '0%';
    if (this.analyserPoll !== null) {
      window.clearInterval(this.analyserPoll);
      this.analyserPoll = null;
    }
    this.log('[RX] Stopped');
  }

  private handleArmBtn(): void {
    // Outer button is a pure disclosure: never stops RX, even when active.
    this.setArmOpen(!this.armWrap.classList.contains('is-open'));
  }

  private handleArmAction(): void {
    if (this.listening || this.playingFile) {
      this.stopRx();
      return;
    }
    this.handleListen();
  }

  private handleListen(): void {
    if (this.listening || this.playingFile) return;
    const cfg = this.getConfig();
    this.spectrum.freqs = cfg.freqs ?? defaultConfig.freqs;
    this.waterfall.freqs = cfg.freqs ?? defaultConfig.freqs;
    this.symbolStream.clear();
    this.outputEl.textContent = '';
    this.tagRow.innerHTML = '';
    this.progressFill.style.width = '0%';
    this.progressValue.textContent = '0%';

    this.receiver.start(cfg).catch((err) => {
      this.log('[RX] start() rejected: ' + (err as Error).message);
      this.tagRow.innerHTML = '';
      this.tagRow.appendChild(this.tag((err as Error).message, 'fail'));
      this.stopRx();
    });
    this.listening = true;
    this.armWrap.dataset.state = 'active';
    this.setArmOpen(false);
    this.armActionBtn.setLabel('STOP RX');
    this.armActionBtn.setVariant('abort');
    this.pollAnalyser();
  }

  private handleImportClick(): void {
    if (this.listening || this.playingFile) return;
    this.fileInput.value = '';
    this.fileInput.click();
  }

  private async handleFileChosen(): Promise<void> {
    const file = this.fileInput.files?.[0];
    if (!file) return;
    if (this.listening) this.stopRx();

    const cfg = this.getConfig();
    this.spectrum.freqs = cfg.freqs ?? defaultConfig.freqs;
    this.waterfall.freqs = cfg.freqs ?? defaultConfig.freqs;
    this.symbolStream.clear();
    this.outputEl.textContent = '';
    this.tagRow.innerHTML = '';
    this.progressFill.style.width = '0%';
    this.progressValue.textContent = '0%';

    this.playingFile = true;
    this.armWrap.dataset.state = 'active';
    this.setArmOpen(false);
    this.armActionBtn.setLabel('STOP RX');
    this.armActionBtn.setVariant('abort');
    this.log(`[RX] Importing ${file.name} (${formatBytes(file.size)}, monitor=${this.monitorToggle.checked})`);

    try {
      await this.receiver.startFromFile(file, cfg, {
        audible: this.monitorToggle.checked,
      });
      this.pollAnalyser();
    } catch (err) {
      this.log('[RX] Import failed: ' + (err as Error).message);
      this.tagRow.innerHTML = '';
      this.tagRow.appendChild(this.tag((err as Error).message, 'fail'));
      this.stopRx();
    }
  }

  private pollAnalyser(): void {
    if (this.analyserPoll !== null) window.clearInterval(this.analyserPoll);
    let attempts = 0;
    this.analyserPoll = window.setInterval(() => {
      const a = this.receiver.getAnalyser();
      if (a) {
        const sr = this.receiver.audioCtx?.sampleRate ?? 48000;
        this.spectrum.bind(a, sr);
        this.waterfall.bind(a, sr);
        this.meter.bind(a);
        if (this.analyserPoll !== null) window.clearInterval(this.analyserPoll);
        this.analyserPoll = null;
        return;
      }
      if (++attempts > 100 && this.analyserPoll !== null) {
        window.clearInterval(this.analyserPoll);
        this.analyserPoll = null;
      }
    }, 50);
  }

  private tag(text: string, tone: 'ok' | 'fail' | 'warn' | 'default'): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'tag';
    if (tone !== 'default') span.dataset.tone = tone;
    span.textContent = text;
    return span;
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
